import type { TagId, CategoryId } from '@/data';

// Tag combination examples for education
export interface TagCombinationExample {
  tagId: TagId;
  categoryId: CategoryId;
  description: string;
  searchTip?: string; // Tips for finding this content
}

// Tutorial definition for each tag
export interface TagTutorialDefinition {
  tagId: TagId;
  tagLabel: string;
  intro: string; // Introduction text explaining the tag
  combinations: TagCombinationExample[];
  proTips: string[]; // Tips for using this tag effectively
}

// Tag tutorial definitions
export const TAG_TUTORIAL_DEFINITIONS: TagTutorialDefinition[] = [
  {
    tagId: 'asian',
    tagLabel: 'Asian',
    intro: 'The "Asian" tag helps couples find content featuring Asian performers. Combine it with categories to find specific scenarios.',
    combinations: [
      { tagId: 'asian', categoryId: 'swallows', description: 'Asian performer swallowing - popular for showing enthusiasm' },
      { tagId: 'asian', categoryId: 'face', description: 'Asian facial content - often showcases beautiful features' },
      { tagId: 'asian', categoryId: 'mouth', description: 'Asian oral finish - typically shows great technique' },
      { tagId: 'asian', categoryId: 'inside', description: 'Asian creampie - intimate and connected content' },
    ],
    proTips: [
      'Combine with "amateur" for authentic couple content',
      'Works well with "pov" for immersive experience',
      'Check for "couple" tag for relationship content'
    ]
  },
  {
    tagId: 'amateur',
    tagLabel: 'Amateur',
    intro: 'The "Amateur" tag identifies real couples and non-professional content. This often feels more authentic and relatable.',
    combinations: [
      { tagId: 'amateur', categoryId: 'inside', description: 'Amateur creampie - real couples, real intimacy' },
      { tagId: 'amateur', categoryId: 'swallows', description: 'Amateur swallowing - genuine reactions and enthusiasm' },
      { tagId: 'amateur', categoryId: 'face', description: 'Amateur facial - authentic, unscripted moments' },
      { tagId: 'amateur', categoryId: 'handjob', description: 'Amateur handjob finish - couples often share these' },
    ],
    proTips: [
      'Amateur content often has better audio/reactions',
      'Look for "couple" tag combination for relationship context',
      'Great for inspiration - feels achievable to try at home'
    ]
  },
  {
    tagId: 'pov',
    tagLabel: 'POV',
    intro: 'Point-of-View (POV) content puts you in the action. Great for immersive viewing and trying positions.',
    combinations: [
      { tagId: 'pov', categoryId: 'mouth', description: 'POV oral finish - see her looking up at you' },
      { tagId: 'pov', categoryId: 'swallows', description: 'POV swallowing - intimate eye contact moment' },
      { tagId: 'pov', categoryId: 'chest', description: 'POV chest finish - see where it lands from his view' },
      { tagId: 'pov', categoryId: 'inside', description: 'POV creampie - intimate connection perspective' },
    ],
    proTips: [
      'POV helps visualize positions before trying them',
      'Combine with "amateur" for authentic POV couples',
      'Great for understanding his perspective'
    ]
  },
  {
    tagId: 'milf',
    tagLabel: 'MILF',
    intro: 'MILF content features experienced, confident performers. Often shows skilled technique and enthusiasm.',
    combinations: [
      { tagId: 'milf', categoryId: 'swallows', description: 'MILF swallowing - confident and skilled' },
      { tagId: 'milf', categoryId: 'face', description: 'MILF facial - embracing and enjoying' },
      { tagId: 'milf', categoryId: 'handjob', description: 'MILF handjob - experienced technique' },
      { tagId: 'milf', categoryId: 'mouth', description: 'MILF oral finish - knows what she\'s doing' },
    ],
    proTips: [
      'MILF content often shows confident body language',
      'Great for learning technique and dirty talk',
      'Often paired with "amateur" for real couples'
    ]
  },
  {
    tagId: 'blonde',
    tagLabel: 'Blonde',
    intro: 'The "Blonde" tag helps find content with blonde performers. A popular appearance preference for many couples.',
    combinations: [
      { tagId: 'blonde', categoryId: 'face', description: 'Blonde facial - contrast shows the finish clearly' },
      { tagId: 'blonde', categoryId: 'swallows', description: 'Blonde swallowing - enthusiastic performances' },
      { tagId: 'blonde', categoryId: 'chest', description: 'Blonde chest finish - popular visual category' },
      { tagId: 'blonde', categoryId: 'allover', description: 'Blonde covered - striking visual contrast' },
    ],
    proTips: [
      'Hair color tags help narrow down preferences',
      'Combine with style tags like "amateur" or "professional"',
      'Partner preferences matter - ask what they like!'
    ]
  },
  {
    tagId: 'redhead',
    tagLabel: 'Redhead',
    intro: 'Redhead content features fiery, passionate performers. Often associated with intense, enthusiastic content.',
    combinations: [
      { tagId: 'redhead', categoryId: 'face', description: 'Redhead facial - beautiful contrast' },
      { tagId: 'redhead', categoryId: 'swallows', description: 'Redhead swallowing - passionate performance' },
      { tagId: 'redhead', categoryId: 'inside', description: 'Redhead creampie - intimate fiery content' },
      { tagId: 'redhead', categoryId: 'mouth', description: 'Redhead oral finish - enthusiastic style' },
    ],
    proTips: [
      'Redheads are rarer - fewer clips but often high quality',
      'Great tag to combine with specific categories',
      'Often associated with passionate performances'
    ]
  },
  {
    tagId: 'couple',
    tagLabel: 'Couple',
    intro: 'The "Couple" tag marks content from real couples in relationships. Perfect for inspiration and ideas to try together.',
    combinations: [
      { tagId: 'couple', categoryId: 'together', description: 'Couple cumming together - the ultimate goal' },
      { tagId: 'couple', categoryId: 'inside', description: 'Couple creampie - intimate relationship content' },
      { tagId: 'couple', categoryId: 'swallows', description: 'Couple swallowing - real partners, real love' },
      { tagId: 'couple', categoryId: 'multiple', description: 'Couple multiple rounds - showing their chemistry' },
    ],
    proTips: [
      'Couple content is most relatable for trying at home',
      'Look for communication and connection in the clips',
      'Great source for realistic expectations and ideas'
    ]
  },
  {
    tagId: 'huge',
    tagLabel: 'Huge Load',
    intro: 'The "Huge Load" tag identifies clips with impressive volume. Perfect for Load Boost inspiration and goals!',
    combinations: [
      { tagId: 'huge', categoryId: 'face', description: 'Huge load facial - maximum visual impact' },
      { tagId: 'huge', categoryId: 'swallows', description: 'Huge load swallow - impressive to take it all' },
      { tagId: 'huge', categoryId: 'chest', description: 'Huge load on chest - covers everything' },
      { tagId: 'huge', categoryId: 'mouth', description: 'Huge load oral - overflowing moments' },
    ],
    proTips: [
      'This is what Load Boost can help you achieve!',
      'Great motivation clips to watch together',
      'Note hydration and timing patterns in these clips'
    ]
  },
  {
    tagId: 'bj',
    tagLabel: 'BJ',
    intro: 'The "BJ" (blowjob) tag marks clips focused on oral technique. Great for learning and inspiration.',
    combinations: [
      { tagId: 'bj', categoryId: 'swallows', description: 'BJ to swallow - the complete oral experience' },
      { tagId: 'bj', categoryId: 'mouth', description: 'BJ oral finish - classic ending' },
      { tagId: 'bj', categoryId: 'face', description: 'BJ to facial - oral then facial finish' },
      { tagId: 'bj', categoryId: 'chest', description: 'BJ to chest - oral then body finish' },
    ],
    proTips: [
      'BJ clips often show different techniques',
      'Pay attention to hand usage and positioning',
      'Audio quality matters for learning dirty talk'
    ]
  },
  {
    tagId: 'latina',
    tagLabel: 'Latina',
    intro: 'Latina content features passionate, expressive performers. Known for enthusiastic and vocal performances.',
    combinations: [
      { tagId: 'latina', categoryId: 'swallows', description: 'Latina swallowing - enthusiastic and vocal' },
      { tagId: 'latina', categoryId: 'face', description: 'Latina facial - expressive reactions' },
      { tagId: 'latina', categoryId: 'ass', description: 'Latina ass finish - often a preference' },
      { tagId: 'latina', categoryId: 'inside', description: 'Latina creampie - passionate and intimate' },
    ],
    proTips: [
      'Latina content often has great audio/reactions',
      'Combine with "amateur" for authentic couples',
      'Known for expressive dirty talk'
    ]
  },
];

// Get tutorial by tag ID
export const getTutorialByTagId = (tagId: string): TagTutorialDefinition | undefined => {
  return TAG_TUTORIAL_DEFINITIONS.find(t => t.tagId === tagId);
};

// Get all available tutorial tag IDs
export const getAvailableTutorialTags = (): string[] => {
  return TAG_TUTORIAL_DEFINITIONS.map(t => t.tagId);
};

// Get next tutorial for a reviewer (returns undefined if all completed)
export const getNextTutorial = (completedTagIds: string[]): TagTutorialDefinition | undefined => {
  return TAG_TUTORIAL_DEFINITIONS.find(t => !completedTagIds.includes(t.tagId));
};
